import { Injectable, signal, computed, effect, PLATFORM_ID, inject, InjectionToken } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export type ThemeMode = 'light' | 'dark' | 'system';

export const THEME_STORAGE_KEY = new InjectionToken<string>('THEME_STORAGE_KEY', {
  factory: () => 'app-theme',
  providedIn: 'root',
});

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private platformId = inject(PLATFORM_ID);
  private storageKeyPrefix = inject(THEME_STORAGE_KEY);

  readonly currentTheme = signal<ThemeMode>('system');
  readonly isDarkMode = signal(false);

  private storageSuffix = signal<string>('');

  private storageKey = computed(() => {
    const suffix = this.storageSuffix();
    return suffix ? `${this.storageKeyPrefix}-${suffix}` : this.storageKeyPrefix;
  });

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.initializeTheme();
      this.listenToSystemChanges();
    }

    effect(() => {
      const theme = this.currentTheme();
      if (isPlatformBrowser(this.platformId)) {
        this.applyTheme(theme);
        localStorage.setItem(this.storageKey(), theme);
      }
    });
  }

  setStorageSuffix(suffix: string): void {
    this.storageSuffix.set(suffix);
    if (isPlatformBrowser(this.platformId)) {
      this.initializeTheme();
    }
  }

  clearStorageSuffix(): void {
    this.storageSuffix.set('');
    if (isPlatformBrowser(this.platformId)) {
      this.initializeTheme();
    }
  }

  private initializeTheme(): void {
    const stored = localStorage.getItem(this.storageKey()) as ThemeMode | null;
    const preference = stored || this.getSystemPreference();
    this.currentTheme.set(preference);
    this.applyTheme(preference);
  }

  private getSystemPreference(): ThemeMode {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
    }
    return 'light';
  }

  private applyTheme(theme: ThemeMode): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const body = document.body;
    body.classList.remove('dark-theme', 'light-theme');

    if (theme === 'system') {
      const prefersDark = window.matchMedia(
        '(prefers-color-scheme: dark)',
      ).matches;
      body.classList.add(prefersDark ? '' : 'light-theme');
      this.isDarkMode.set(prefersDark);
    } else {
      body.classList.add(`${theme}-theme`);
      this.isDarkMode.set(theme === 'dark');
    }
  }

  toggle(): void {
    const current = this.isDarkMode();
    this.currentTheme.set(current ? 'light' : 'dark');
  }

  setTheme(theme: ThemeMode): void {
    this.currentTheme.set(theme);
  }

  private listenToSystemChanges(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    window
      .matchMedia('(prefers-color-scheme: dark)')
      .addEventListener('change', () => {
        if (this.currentTheme() === 'system') {
          this.applyTheme('system');
        }
      });
  }
}
